'use client';

import { useMemo } from 'react';

import { formatUnits } from 'viem';
import { useReadContract, useChainId } from 'wagmi';

import { poolContractAbi } from '@/abi';
import ChartSection from '@/components/shared/ChartSection';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_Pool } from '@/ts/types/T_Pool';

import type { I_MarketData } from '@/server/actions/market/getHistory.action';
import type { T_Symbol, T_Timeframe } from '@/ts/types/T_Chart';
import type { Address } from 'viem';

interface I_PoolChartSectionWrapperProps {
  marketData: I_MarketData;
  symbol: T_Symbol | string;
  timeframe: T_Timeframe;
  url: string;
  poolDecimals: number;
  poolSymbols?: string[];
  poolSlugs?: string[];
  currentSlug?: string;
  pool?: T_Pool;
  oldPrice: number;
  newPrice: number;
}

export default function PoolChartSectionWrapper({
  marketData: initialMarketData,
  symbol,
  timeframe,
  url,
  poolDecimals,
  poolSymbols,
  poolSlugs,
  currentSlug,
  pool,
  oldPrice,
  newPrice
}: I_PoolChartSectionWrapperProps) {
  const chainId = useChainId();

  // Check if oldPrice === newPrice
  const shouldFetchFromContract = useMemo(() => {
    return oldPrice === newPrice && oldPrice > 0 && newPrice > 0;
  }, [oldPrice, newPrice]);

  // Default to active network when wallet is not connected
  const activeChainId = chainId || C_ACTIVE_NETWORK_ID;

  // Fetch price from contract if oldPrice === newPrice
  const { data: contractTokenPrice } = useReadContract({
    address: pool?.poolAddress as Address | undefined,
    abi: poolContractAbi,
    functionName: 'getTokenPrice',
    chainId: activeChainId,
    query: {
      enabled: shouldFetchFromContract && !!pool?.poolAddress
    }
  }) as { data: bigint | undefined };

  // Calculate updated marketData with contract price if needed
  const marketData = useMemo(() => {
    if (shouldFetchFromContract && contractTokenPrice !== undefined) {
      // Convert bigint to decimal string using USDC decimals (6)
      const contractPrice = formatUnits(contractTokenPrice, C_USDC_DECIMALS);
      const contractPriceNum = parseFloat(contractPrice);

      // Calculate price change and percent using contract price as newPrice and oldPrice
      const changedPrice = contractPriceNum - oldPrice;
      const changedPercent =
        oldPrice > 0 ? ((changedPrice / oldPrice) * 100).toFixed(2) : '0';

      return {
        ...initialMarketData,
        tickerPrice: {
          ...initialMarketData.tickerPrice,
          price: contractPrice,
          priceChange: `${changedPrice}`,
          priceChangePercent: changedPercent
        }
      };
    }

    // If contract call is still loading, use initial marketData but keep old price logic
    // If prices are not equal, use initial marketData as-is
    return initialMarketData;
  }, [
    shouldFetchFromContract,
    contractTokenPrice,
    oldPrice,
    initialMarketData
  ]);

  return (
    <ChartSection
      url={url}
      symbol={symbol as T_Symbol}
      marketData={marketData}
      timeframe={timeframe}
      type="pool"
      poolDecimals={poolDecimals}
      poolSymbols={poolSymbols}
      poolSlugs={poolSlugs}
      currentSlug={currentSlug}
      pool={pool}
    />
  );
}
